/**
 * Article Loader Service
 * Loads and manages article JSON files from the /articles folder
 * 
 * This service provides:
 * - Loading all articles at build time
 * - Retrieving articles by slug, category, or search
 * - Validating article structure
 * - Caching for performance
 */

// This will be populated at build time with article data
let articlesCache = null;

/**
 * Load all articles from the articles folder
 * In production, articles are bundled at build time
 */
export const loadAllArticles = async () => {
  if (articlesCache && articlesCache.length > 0) {
    return articlesCache;
  }

  try {
    const module = await import(
      /* webpackChunkName: "articles-data" */
      '../data/articlesMetadata.js'
    );

    // Support both named export and default export
    const articlesData = module.articles || module.default || [];
    articlesCache = Array.isArray(articlesData) ? articlesData : [];


    return articlesCache;
  } catch (error) {
    console.error('❌ CRITICAL: Could not load articles metadata:', error);
    return [];
  }
};

/**
 * Get a single article by slug
 */
export const getArticleBySlug = async (slug) => {
  const articles = await loadAllArticles();
  return articles.find(article => article.slug === slug);
};

/**
 * Get articles by category
 */
export const getArticlesByCategory = async (category) => {
  const articles = await loadAllArticles();
  if (category === 'All') {
    return articles;
  }
  return articles.filter(article => article.category === category);
};

/**
 * Search articles by query
 */
export const searchArticles = async (query) => {
  if (!query || query.trim().length === 0) {
    return [];
  }

  const articles = await loadAllArticles();
  const searchTerm = query.toLowerCase();

  return articles.filter(article =>
    article.title.toLowerCase().includes(searchTerm) ||
    article.excerpt.toLowerCase().includes(searchTerm) ||
    article.category.toLowerCase().includes(searchTerm) ||
    (article.seoKeywords && article.seoKeywords.join(' ').toLowerCase().includes(searchTerm)) ||
    (article.tags && article.tags.join(' ').toLowerCase().includes(searchTerm))
  );
};

/**
 * Get articles by tag
 */
export const getArticlesByTag = async (tag) => {
  const articles = await loadAllArticles();
  return articles.filter(article =>
    article.tags && article.tags.includes(tag)
  );
};

/**
 * Get related articles based on category, tags, or keywords
 */
export const getRelatedArticles = async (slug, limit = 4) => {
  const articles = await loadAllArticles();
  const currentArticle = articles.find(a => a.slug === slug);

  if (!currentArticle) {
    return [];
  }

  const related = articles.filter(article =>
    article.slug !== slug && (
      article.category === currentArticle.category ||
      (article.tags && currentArticle.tags &&
        article.tags.some(tag => currentArticle.tags.includes(tag)))
    )
  );

  return related.slice(0, limit);
};

/**
 * Get all unique categories
 */
export const getAllCategories = async () => {
  const articles = await loadAllArticles();
  const categories = new Set(articles.map(a => a.category));
  return Array.from(categories).sort();
};

/**
 * Get all unique tags
 */
export const getAllTags = async () => {
  const articles = await loadAllArticles();
  const tags = new Set();
  articles.forEach(article => {
    if (article.tags && Array.isArray(article.tags)) {
      article.tags.forEach(tag => tags.add(tag));
    }
  });
  return Array.from(tags).sort();
};

/**
 * Get pagination info for articles
 */
export const getPaginatedArticles = async (page = 1, perPage = 12, category = 'All') => {
  const articles = await getArticlesByCategory(category);
  const totalPages = Math.ceil(articles.length / perPage);
  const startIndex = (page - 1) * perPage;
  const paginatedArticles = articles.slice(startIndex, startIndex + perPage);

  return {
    articles: paginatedArticles,
    currentPage: page,
    totalPages,
    totalArticles: articles.length,
    perPage
  };
};

/**
 * Validate article structure
 */
export const validateArticle = (article) => {
  const requiredFields = [
    'id', 'slug', 'title', 'excerpt', 'category',
    'quickAnswer', 'expandedAnswer', 'keyPoints', 'content',
    'faqItems', 'readTime', 'date', 'tags', 'seoKeywords'
  ];

  const errors = [];

  requiredFields.forEach(field => {
    if (!article[field]) {
      errors.push(`Missing required field: ${field}`);
    }
  });

  // Validate field types and lengths
  if (article.title && article.title.length < 20) {
    errors.push('Title too short (minimum 20 characters)');
  }

  if (article.excerpt && article.excerpt.length < 100) {
    errors.push('Excerpt too short (minimum 100 characters)');
  }

  if (article.content && article.content.length < 1000) {
    errors.push('Content too short (minimum 1000 characters)');
  }

  if (article.keyPoints && article.keyPoints.length < 5) {
    errors.push('Minimum 5 key points required');
  }

  if (article.faqItems && article.faqItems.length < 3) {
    errors.push('Minimum 3 FAQ items required');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Get article statistics
 */
export const getArticleStats = async () => {
  const articles = await loadAllArticles();
  const categories = new Set();
  const tags = new Set();
  let totalWords = 0;
  let totalReadTime = 0;

  articles.forEach(article => {
    categories.add(article.category);
    if (article.tags) {
      article.tags.forEach(tag => tags.add(tag));
    }

    // Rough word count
    if (article.content) {
      totalWords += article.content.split(/\s+/).length;
    }

    // Parse read time
    const readTimeMatch = article.readTime?.match(/(\d+)/);
    if (readTimeMatch) {
      totalReadTime += parseInt(readTimeMatch[1]);
    }
  });

  return {
    totalArticles: articles.length,
    totalCategories: categories.size,
    totalTags: tags.size,
    totalWords,
    averageReadTime: Math.round(totalReadTime / articles.length),
    categories: Array.from(categories),
    tags: Array.from(tags)
  };
};

export default {
  loadAllArticles,
  getArticleBySlug,
  getArticlesByCategory,
  searchArticles,
  getArticlesByTag,
  getRelatedArticles,
  getAllCategories,
  getAllTags,
  getPaginatedArticles,
  validateArticle,
  getArticleStats
};
